import { useEffect, useRef } from 'react'
import { TimelineEditor } from './TimelineEditor'
import { Play3, Pause } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './AudioEditorModal.module.scss'
import type { MediaObject, AudioAsset } from 'types'
import { useVideoPlayback } from './hooks/useVideoPlayback'
import { useAudioPlayback } from './hooks/useAudioPlayback'

interface AudioTimelineStepProps {
  mediaObject: MediaObject
  selectedAudio?: AudioAsset | null
  musicStartTime: number
  isVideoMuted: boolean
  hasOriginalAudio: boolean
  hasExistingMusicTrack: boolean
  onMusicStartTimeChange: (time: number) => void
  onToggleVideoMute: () => void
  onReplaceAudio: () => void
  onRemoveAudio: () => void
}

const getOriginalAudioUrl = (mediaObject: MediaObject): string | null => {
  const tracks = mediaObject.audio?.tracks
  if (!tracks) return null

  const originalTrack = tracks.find((track) => track.type === 'original')
  return originalTrack?.url || null
}

export const AudioTimelineStep = ({
  mediaObject,
  selectedAudio,
  musicStartTime,
  isVideoMuted,
  hasOriginalAudio,
  hasExistingMusicTrack,
  onMusicStartTimeChange,
  onToggleVideoMute,
  onReplaceAudio,
  onRemoveAudio,
}: AudioTimelineStepProps) => {
  const videoDuration = mediaObject.duration || 0
  const musicEndTime = musicStartTime + (selectedAudio?.duration || 0)
  const audioStartedRef = useRef(false)
  const prevMusicStartTimeRef = useRef(musicStartTime)
  const originalAudioUrl = getOriginalAudioUrl(mediaObject)

  const { videoRef, currentTime, isPlaying, handlePlayPause, handleStop, handleTimeChange } = useVideoPlayback({
    videoDuration,
    videoUrl: mediaObject.source_url,
  })

  const { playAudioFromTime: playMusicFromTime, stopAudio: stopMusic } = useAudioPlayback({
    audioUrl: selectedAudio?.preview_url || '',
    musicStartTime,
    musicEndTime,
    videoDuration,
  })

  // Original audio playback (plays from 0 to videoDuration)
  const { playAudioFromTime: playOriginalAudioFromTime, stopAudio: stopOriginalAudio } = useAudioPlayback({
    audioUrl: originalAudioUrl || '',
    musicStartTime: 0,
    musicEndTime: videoDuration,
    videoDuration,
  })

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true
    }
  }, [videoRef])

  useEffect(() => {
    if (!isPlaying || prevMusicStartTimeRef.current !== musicStartTime) {
      audioStartedRef.current = false
      prevMusicStartTimeRef.current = musicStartTime
    }
  }, [isPlaying, musicStartTime])

  useEffect(() => {
    if (isPlaying) {
      // Only play music if selectedAudio exists
      if (selectedAudio) {
        playMusicFromTime(currentTime)
      }

      // Only play original audio if not muted and it exists
      if (!isVideoMuted && originalAudioUrl) {
        playOriginalAudioFromTime(currentTime)
      } else {
        stopOriginalAudio()
      }
    } else {
      stopMusic()
      stopOriginalAudio()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, isVideoMuted, selectedAudio])

  useEffect(() => {
    if (!isPlaying || audioStartedRef.current || !selectedAudio) return

    const effectiveMusicStartTime = Math.max(0, musicStartTime)

    // Check if we've reached or passed the music start time
    if (currentTime >= effectiveMusicStartTime && currentTime < musicEndTime) {
      audioStartedRef.current = true
      playMusicFromTime(currentTime)
    }
  }, [currentTime, isPlaying, musicStartTime, musicEndTime, playMusicFromTime, selectedAudio])

  const handlePausePlayback = () => {
    if (isPlaying) {
      handlePlayPause()
    }
  }

  const handleResumePlayback = () => {
    if (!isPlaying) {
      handlePlayPause()
    }
  }

  const handleTimeChangeWithAudioSync = (time: number) => {
    handleTimeChange(time)
    if (isPlaying) {
      if (selectedAudio) {
        playMusicFromTime(time)
      }
      if (!isVideoMuted && originalAudioUrl) {
        playOriginalAudioFromTime(time)
      }
    }
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className={Styles.timelineStep}>
      <div className={Styles.videoPreviewContainer}>
        <div className={Styles.videoBackground}>
          <video ref={videoRef} src={mediaObject.source_url} className={Styles.videoPreview} onEnded={handleStop} />
        </div>

        <div className={Styles.playbackControls}>
          <IconButton
            className={Styles.playPauseButton}
            icon={isPlaying ? Pause : Play3}
            onPress={handlePlayPause}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            variant="secondary"
            size="md"
          />
          <Text variant="secondary" color="var(--dark-80)">
            {formatTime(currentTime)} <span style={{ color: 'var(--dark-40)' }}>/ {formatTime(videoDuration)}</span>
          </Text>
        </div>
      </div>

      <TimelineEditor
        videoUrl={mediaObject.source_url}
        videoDuration={videoDuration}
        currentTime={currentTime}
        onTimeChange={handleTimeChangeWithAudioSync}
        selectedAudio={selectedAudio}
        musicStartTime={musicStartTime}
        onMusicStartTimeChange={onMusicStartTimeChange}
        onPausePlayback={handlePausePlayback}
        onResumePlayback={handleResumePlayback}
        isPlaying={isPlaying}
        isVideoMuted={isVideoMuted}
        onToggleVideoMute={onToggleVideoMute}
        hasOriginalAudio={hasOriginalAudio}
        hasExistingMusicTrack={hasExistingMusicTrack}
        onReplaceAudio={onReplaceAudio}
        onRemoveAudio={onRemoveAudio}
      />
    </div>
  )
}
